import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    BatImpactChart, number, pitchSubTypeColor, HeadingChart, Checkbox, pitchTypeOrSubType,
    pitchTypeDisplayName, MultiSelect, Button, Dropdown, LoadingSpinner, ToggleButtons,
} from 'yankees-react-utils';
import ReportCard from '../../../components/ReportCard';
import {
    COUNT_BUCKET,
    matchesCountBucket,
    getTrackedSwings,
    findBatTrackingEllipse,
    buildLeagueHeadingByPitchType,
    getCountBucketToggleOptions,
} from './batTrackingCountBucket';

const StyledCheckboxWrapper = styled.div`
    .nyy-checkbox-radio-container {
        display: flex;
    }
    & input[type="checkbox"]:checked + label::before {
        background-color: ${(props) => props.$color} !important;
        border-color: ${(props) => props.$color} !important;
    }
    & input[type="checkbox"] + label::before {
        border-radius: 100%;
    }
`;

const StyledToggleButtons = styled(ToggleButtons)`
    & .btn {
        font-size: 0.8rem !important;
        min-height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
    }

    & .btn-check:not(:checked) + .btn {
        color: #204b76 !important;
        background-color: #eeeeee !important;
    }
`;

const StyledHeader = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 0.75rem;

    .matchup-bat-tracking-toggle {
        justify-self: center;
    }

    .matchup-bat-tracking-filters {
        display: flex;
        justify-content: flex-end;
        justify-self: end;
    }

    @media (max-width: 991.98px) {
        grid-template-columns: minmax(0, 1fr);

        .matchup-bat-tracking-filters {
            justify-content: center;
            justify-self: stretch;
            width: 100%;
        }
    }
`;

const buildPitchTypesSeries = (
    pitches,
    repertoire,
) => repertoire.map((r) => {
    const pitchType = pitchTypeDisplayName(r);
    return {
        id: pitchType,
        name: pitchType,
        color: pitchSubTypeColor({
            type: r.fxtype,
            fxsubtype: r.fxsubtype,
        }),
        data: pitches.filter((p) => p.fxtype === r.fxtype && p.fxsubtype === r.fxsubtype)
            .map((p) => ({
                fxtype: p.fxtype,
                fxsubtype: p.fxsubtype,
                hbat: p.hbat,
                vbat: p.vbat,
                heading: p.heading,
                marker: {
                    symbol: 'circle',
                    radius: 5,
                    lineWidth: 1,
                    lineColor: '#fff',
                    states: { hover: { radiusPlus: 10, lineWidth: 2, lineColor: '#fff' } },
                },
            })),
    };
});

const getLevelOptions = (battingSeasons) => {
    const extra = [{ Abbr: 'MIN', LevelID: 7, SortOrder: 2 }];

    const levels = battingSeasons.map((bs) => bs.Level);

    return extra.concat([...new Set(levels.map((l) => l.LevelID))].map((levelId) => levels.find((l) => l.LevelID === levelId)))
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((level) => ({
            name: level.Abbr,
            value: level.LevelID,
        }));
};

const pitchOrders = [10, 9, 8, 33, 3, 34, 4, 7, 2];
const getPitchOrderValue = (pt) => {
    const index = pitchOrders.indexOf(pt);

    return index > -1 ? index : 100 + pt;
};

const isSamePitchType = (first, second) => first.fxtype === second.fxtype && first.fxsubtype === second.fxsubtype;

export default function MatchupReportBatTracking({
    isRepertoireLoading,
    isLhb,
    isLhp,
    repertoire,
    currentYear,
    isHandedness,
    batTrackingData: batTracking,
    isBatTrackingFetching,
    battingSeasons,
    isBattingSeasonsFetching,
    levels,
    yearId,
    onApplyFilters,
}) {
    const { t: translate } = useTranslation();
    const yearOptions = [
        { name: currentYear, value: currentYear },
        { name: `${currentYear - 1} - ${currentYear}`, value: -1 },
        { name: currentYear - 1, value: currentYear - 1 },
    ];

    const [innerLevels, setInnerLevels] = useState(levels);
    const [innerYearId, setInnerYearId] = useState(yearId);
    const [countBucket, setCountBucket] = useState(COUNT_BUCKET.OVERALL);
    const [customSelectedPitchTypes, setCustomSelectedPitchTypes] = useState(null);

    const hitterBatTracking = batTracking?.hitterbattracking;

    const batterRepertoire = useMemo(
        () => (hitterBatTracking ?? [])
            .filter((r) => matchesCountBucket(r.countbucket, COUNT_BUCKET.OVERALL))
            .map((r) => ({ fxtype: r.fxtype, fxsubtype: r.fxsubtype })),
        [hitterBatTracking],
    );

    const repertoireToUse = useMemo(() => (isHandedness
        ? batterRepertoire
        : (repertoire ?? []).map((r) => ({
            fxtype: r.FXType,
            fxsubtype: r.FXSubType || null,
        }))),
    [isHandedness, batterRepertoire, repertoire]);

    const defaultSelectedPitchTypes = useMemo(
        () => repertoireToUse
            .filter((r) => r.fxtype > 1)
            .map((r) => ({ fxtype: r.fxtype, fxsubtype: r.fxsubtype })),
        [repertoireToUse],
    );
    const selectedPitchTypes = (customSelectedPitchTypes ?? defaultSelectedPitchTypes)
        .filter((selectedPitchType) => repertoireToUse.some((pitchType) => isSamePitchType(selectedPitchType, pitchType)));
    const levelScope = [...(levels ?? [])].sort((a, b) => a - b).join(',');

    useEffect(() => {
        setInnerLevels(levels);
        setInnerYearId(yearId);
    }, [levels, yearId]);

    useEffect(() => {
        setCustomSelectedPitchTypes(null);
    }, [levelScope, yearId]);

    const applyFilter = () => {
        onApplyFilters({ levels: innerLevels, yearId: innerYearId });
    };

    const orderedPitchTypes = repertoireToUse.filter((r) => r.fxtype > 1)
        .sort((a, b) => getPitchOrderValue(b.fxsubtype || b.fxtype) - getPitchOrderValue(a.fxsubtype || a.fxtype));
    const filteredOrderedPitchTypes = repertoireToUse
        .filter((r) => r.fxtype > 1 && selectedPitchTypes.some((spt) => spt.fxtype === r.fxtype && spt.fxsubtype === r.fxsubtype))
        .sort((a, b) => getPitchOrderValue(b.fxsubtype || b.fxtype) - getPitchOrderValue(a.fxsubtype || a.fxtype));
    const filteredPitches = (hitterBatTracking ?? [])
        .filter((p) => matchesCountBucket(p.countbucket, countBucket)
            && selectedPitchTypes.some((spt) => spt.fxtype === p.fxtype && spt.fxsubtype === p.fxsubtype));

    const series = buildPitchTypesSeries(filteredPitches, filteredOrderedPitchTypes);
    const headingSeries = series.map((s) => ({
        ...s,
        data: s.data.filter((x) => x.heading !== null).map((d) => ({
            ...d,
            x: d.heading,
            y: filteredOrderedPitchTypes.findIndex(((r) => r.fxtype === d.fxtype && r.fxsubtype === d.fxsubtype)),
        })),
    }));
    const batImpactSeries = series.filter((x) => x.hbat !== null && x.vbat !== null).map((s) => ({
        ...s,
        data: s.data.map((d) => ({
            ...d,
            x: d.hbat,
            y: d.vbat,
        })),
    }));

    const pitchTypeLeagueAverages = useMemo(() => filteredOrderedPitchTypes
        .map((spt) => {
            const match = findBatTrackingEllipse(batTracking?.hbatvbatellipses, {
                isLhb, isLhp, fxtype: spt.fxtype, fxsubtype: spt.fxsubtype, countBucket,
            });
            if (match?.centerx == null) {
                return null;
            }
            return {
                fxtype: spt.fxtype,
                fxsubtype: spt.fxsubtype,
                x: match.centerx,
                y: match.centerz,
                minorRadius: match.minorrad,
                majorRadius: match.majorrad,
                angle: match.angle,
            };
        }).filter((x) => !!x), [batTracking, isLhp, isLhb, filteredOrderedPitchTypes, countBucket]);

    const leagueHeadingByPitchType = useMemo(() => buildLeagueHeadingByPitchType(
        filteredOrderedPitchTypes,
        batTracking?.hbatvbatellipses,
        { isLhb, isLhp, countBucket },
    ), [filteredOrderedPitchTypes, batTracking, isLhb, isLhp, countBucket]);

    const onPitchTypeClick = (pitchType) => {
        setCustomSelectedPitchTypes((current) => {
            const currentSelection = (current ?? defaultSelectedPitchTypes)
                .filter((selectedPitchType) => repertoireToUse.some((type) => isSamePitchType(selectedPitchType, type)));
            if (currentSelection.some((selectedPitchType) => isSamePitchType(selectedPitchType, pitchType))) {
                return currentSelection.filter((selectedPitchType) => !isSamePitchType(selectedPitchType, pitchType));
            }
            return [...currentSelection, pitchType];
        });
    };

    const headingTooltipFormatter = (s, p) => `<b>${s.name}</b><br />Heading: ${number(p.heading, 1)}`;
    const batImpactTooltipFormatter = (s, p) => `<b>${s.name}</b><br />HBat: ${number(p.hbat, 1)}<br />VBat: ${number(p.vbat, 1)}`;

    const isLoading = isBatTrackingFetching || isRepertoireLoading;
    const hasData = (hitterBatTracking ?? []).some((t) => !!t.trackedswings);

    return (
        <ReportCard>
            <StyledHeader>
                <div>
                    <h4 className="mb-0">{translate('pitcher-overview.sections.bat-tracking')}</h4>
                </div>
                <div className="matchup-bat-tracking-toggle d-flex align-items-center gap-2">
                    <StyledToggleButtons
                        type="radio"
                        name="matchup-bat-tracking-count-bucket"
                        options={getCountBucketToggleOptions(translate)}
                        separated
                        value={countBucket}
                        onChange={(value) => setCountBucket(Number(value))}
                    />
                </div>
                <div className="matchup-bat-tracking-filters">
                    {!window.$IsDugoutApp && (
                        <div className="d-flex flex-wrap gap-4 align-items-center justify-content-center flex-shrink-0">

                            {isBattingSeasonsFetching
                                ? <LoadingSpinner size="sm" />
                                : (
                                    <MultiSelect
                                        style={{ maxWidth: 150 }}
                                        setValue={setInnerLevels}
                                        value={innerLevels}
                                        options={getLevelOptions(battingSeasons ?? [])}
                                    />
                                )}
                            <Dropdown
                                options={yearOptions}
                                value={yearOptions.find((yo) => yo.value === innerYearId)}
                                onSelect={(v) => setInnerYearId(v.value)}
                            />
                            <Button variant="primary" onClick={applyFilter}>{translate('common.filter')}</Button>
                        </div>
                    )}
                </div>
            </StyledHeader>
            {isLoading ? <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}><LoadingSpinner /></div>
                : (
                    <div className="py-2 mt-3">
                        {hasData ? (
                            <div className="d-flex justify-content-around">
                                <div className="text-center">
                                    <div className="fs-4 fw-bold">{translate('pitcher-overview.sections.bat-tracking-timing')}</div>
                                    <HeadingChart
                                        seriesData={headingSeries}
                                        orderedPitchTypes={filteredOrderedPitchTypes.map((pt) => pt.fxsubtype || pt.fxtype)}
                                        tooltipFormatter={headingTooltipFormatter}
                                        showLeagueAverageEllipses
                                        leagueHeadingByPitchType={leagueHeadingByPitchType}
                                        disableJitter
                                        sizeMultiplier={0.8}
                                        min={-20}
                                        max={50}
                                    />
                                    <div className="d-flex justify-content-between ms-2 ps-5 pe-3">
                                        <div className="d-flex align-items-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                height="29px"
                                                viewBox="0 -960 960 960"
                                            >
                                                <path d="M360-200 80-480l280-280 56 56-183 184h647v80H233l184 184-57 56Z" />
                                            </svg>
                                            <div className="fs-4 fw-bold ms-1">{translate('pitcher-overview.sections.bat-tracking-late')}</div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="fs-4 fw-bold me-1">{translate('pitcher-overview.sections.bat-tracking-early')}</div>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                height="29px"
                                                viewBox="0 -960 960 960"
                                            >
                                                <path d="m600-200-57-56 184-184H80v-80h647L544-704l56-56 280 280-280 280Z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center d-flex flex-column justify-content-between align-items-center">
                                    <div className="fs-4 fw-bold">{translate('pitcher-overview.sections.bat-tracking-bat-locations')}</div>
                                    <BatImpactChart
                                        seriesData={batImpactSeries}
                                        isLhb={isLhb}
                                        pitcherView
                                        tooltipFormatter={batImpactTooltipFormatter}
                                        pitchTypeLeagueAverages={pitchTypeLeagueAverages}
                                        zoom
                                        sizeMultiplier={0.8}
                                    />
                                </div>
                            </div>
                        ) : <div className="py-5 text-center fs-6">{translate('pitcher-overview.no-data')}</div>}
                        <div className="d-flex justify-content-center align-items-center pb-2 mt-4 d-print-none">
                            {orderedPitchTypes.toReversed().map((pt) => (
                                <div className="d-flex align-items-center mx-2" key={`${pt.fxtype}-${pt.fxsubtype}`}>
                                    <StyledCheckboxWrapper $color={pitchSubTypeColor(pt)}>
                                        <Checkbox
                                            id={`matchup-bat-tracking-${pitchTypeOrSubType(pt.fxsubtype || pt.fxtype)}`}
                                            checked={selectedPitchTypes
                                                .some((spt) => spt.fxtype === pt.fxtype && spt.fxsubtype === pt.fxsubtype)}
                                            onClick={() => onPitchTypeClick(pt)}
                                        />
                                    </StyledCheckboxWrapper>
                                    <Button
                                        variant="link"
                                        onClick={() => setCustomSelectedPitchTypes([{ fxtype: pt.fxtype, fxsubtype: pt.fxsubtype }])}
                                        className="ps-0"
                                    >
                                        {pitchTypeOrSubType(pt.fxsubtype || pt.fxtype, true, null, translate)}{' '}
                                        ({getTrackedSwings(hitterBatTracking ?? [], pt, countBucket)})
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
        </ReportCard>
    );
}
